import { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const beliefs = [
  "We believe the world belongs to those who architect their position within it.",
  "We believe that where you were born does not determine where you will operate.",
  "We believe that taxes are not a destiny, that borders are not a ceiling, and that citizenship is a strategic variable, not merely a birthright.",
  "We believe that the most powerful thing a founder, executive, or family can do in an uncertain world is build optionality. More than one jurisdiction. More than one residency footprint. More than one layer of protection.",
];

const declarations = [
  "We are not running from anywhere. We are running toward structural clarity.",
  "We are not looking for loopholes. We are building architecture.",
  "Strategic freedom is not radical. It is engineered.",
];

export default function ManifestoPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#f7f6f3]" data-testid="manifesto-page">
      {/* Hero */}
      <div className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-[#0b1a1a]">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle at 30% 40%, #1a7a7a 0%, transparent 50%), radial-gradient(circle at 70% 60%, #1a7a7a 0%, transparent 50%)',
          }}
        />
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center relative z-10">
          <p
            className="text-sm uppercase tracking-[0.4em] text-[#1a7a7a] mb-6 font-semibold"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Our Manifesto
          </p>
          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-white leading-[0.95]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            The Global<br />
            Citizen<br />
            <span className="text-[#1a7a7a]">Manifesto</span>
          </h1>
        </div>
      </div>

      {/* Beliefs */}
      <div className="container mx-auto px-4 md:px-8 max-w-3xl pb-16 md:pb-24 pt-16 md:pt-24">
        <div className="space-y-12 md:space-y-16">
          {beliefs.map((text, i) => (
            <p
              key={i}
              className="text-xl md:text-2xl lg:text-3xl text-gray-600 leading-relaxed font-light"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {text}
            </p>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="flex justify-center py-8">
        <div className="w-24 h-px bg-[#1a7a7a]" />
      </div>

      {/* Declarations */}
      <div className="container mx-auto px-4 md:px-8 max-w-3xl py-16 md:py-24">
        <div className="space-y-12 md:space-y-16">
          {declarations.map((text, i) => (
            <p
              key={i}
              className="text-xl md:text-2xl lg:text-3xl text-[#1a1a1a] leading-relaxed font-semibold"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {text}
            </p>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="flex justify-center py-8">
        <div className="w-24 h-px bg-[#1a7a7a]" />
      </div>

      {/* Closing */}
      <div className="container mx-auto px-4 md:px-8 max-w-3xl py-16 md:py-24">
        <p
          className="text-2xl md:text-3xl lg:text-4xl text-[#1a1a1a] leading-snug font-bold mb-16"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          We are <span className="text-[#1a7a7a]">Global Citizens</span>. We operate by design, not by default.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href="https://calendly.com/chr-globalcitizen/video-consultation-clone"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="manifesto-home-cta"
            className="bg-[#1a7a7a] text-white font-bold px-8 py-4 rounded-full text-base hover:bg-[#1a6868] transition-all duration-300 inline-flex items-center justify-center gap-2"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Request an Introduction <ArrowRight size={18} />
          </a>
          <Link
            to="/"
            data-testid="manifesto-firm-cta"
            className="border border-gray-300 text-gray-600 hover:text-[#1a1a1a] hover:border-gray-400 font-medium px-8 py-4 rounded-full text-base transition-all duration-300 inline-flex items-center justify-center"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            onClick={() => {
              setTimeout(() => {
                document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' });
              }, 300);
            }}
          >
            The Firm
          </Link>
        </div>

        {/* Family Office pointer */}
        <div
          data-testid="manifesto-family-office-pointer"
          className="mt-16 pt-10 border-t border-gray-200"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          <p className="text-gray-500 uppercase text-xs tracking-[0.14em] mb-3 font-medium">
            Family Office Practice
          </p>
          <p className="text-gray-500 text-base leading-relaxed max-w-[62ch] mb-4">
            For families whose lives, companies and assets sit in more than one country, the co-founders lead a dedicated Family Office Practice.
          </p>
          <a
            href="https://familyoffice.globalcitizenconsult.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="manifesto-family-office-link"
            className="text-[#1a7a7a] font-semibold text-[15px] hover:text-[#1a6868] transition-colors duration-300"
          >
            Visit the Family Office practice →
          </a>
        </div>
      </div>

      <div className="h-16" />
    </div>
  );
}
